import { shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const localeLoaders = {
  de: () =>
    Promise.all([
      import('emojibase-data/de/compact.json'),
      import('emojibase-data/de/messages.json'),
    ]),
  en: () =>
    Promise.all([
      import('emojibase-data/en/compact.json'),
      import('emojibase-data/en/messages.json'),
    ]),
  es: () =>
    Promise.all([
      import('emojibase-data/es/compact.json'),
      import('emojibase-data/es/messages.json'),
    ]),
  fr: () =>
    Promise.all([
      import('emojibase-data/fr/compact.json'),
      import('emojibase-data/fr/messages.json'),
    ]),
  it: () =>
    Promise.all([
      import('emojibase-data/it/compact.json'),
      import('emojibase-data/it/messages.json'),
    ]),
  pt: () =>
    Promise.all([
      import('emojibase-data/pt/compact.json'),
      import('emojibase-data/pt/messages.json'),
    ]),
};

const normalizeLocale = locale => {
  const language = locale?.replace('_', '-').split('-')[0].toLowerCase();
  return localeLoaders[language] ? language : 'en';
};

const normalizeUnicode = unicode => unicode.replaceAll('\uFE0F', '');

const buildLocaleData = ([compactModule, messagesModule]) => {
  const compact = compactModule.default;
  const messages = messagesModule.default;
  const emojis = compact.flatMap(emoji => [emoji, ...(emoji.skins || [])]);

  return {
    emojis: new Map(
      emojis.map(emoji => [normalizeUnicode(emoji.unicode), emoji])
    ),
    groups: messages.groups
      .filter(group => group.key !== 'component')
      .sort((left, right) => left.order - right.order)
      .map(group => group.message),
  };
};

const localeDataCache = new Map();

export const loadEmojiLocaleData = locale => {
  const language = normalizeLocale(locale);
  if (!localeDataCache.has(language)) {
    localeDataCache.set(
      language,
      localeLoaders[language]().then(buildLocaleData)
    );
  }

  return localeDataCache.get(language);
};

export const useEmojiLocaleData = enabled => {
  const { locale: i18nLocale } = useI18n();
  const locale = i18nLocale || shallowRef('en');
  const localeData = shallowRef(null);
  let requestId = 0;

  watch(
    [locale, enabled],
    async ([currentLocale, isEnabled]) => {
      requestId += 1;
      const currentRequestId = requestId;
      if (!isEnabled) {
        localeData.value = null;
        return;
      }

      const data = await loadEmojiLocaleData(currentLocale);
      if (currentRequestId === requestId) localeData.value = data;
    },
    { immediate: true }
  );

  return localeData;
};

export const emojiLocaleKey = normalizeUnicode;
